import gsap from 'gsap';

const ColorMediaColumns = (): void => {
  const block = document.querySelector<HTMLElement>('.m-colored-media-columns');
  const rightCards = document.querySelector<HTMLElement>('.right-column-cards');
  const cards = document.querySelectorAll<HTMLDivElement>('.card-desktop');
  const videos = document.querySelectorAll<HTMLVideoElement>('.video');
  const images = document.querySelectorAll<HTMLImageElement>('.image');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!block || !rightCards) return;
  let lastScrollY = window.scrollY;
  let isDesktop = window.matchMedia('(min-width: 1024px)').matches;
  let cleanupFunction: Array<() => void> = [];
  let currentActiveIndex = 0;
  const ratios: number[] = new Array(cards.length).fill(0);

  function setActiveMedia(index: number): void {
    const isDesktopNow = window.matchMedia('(min-width: 1024px)').matches;

    images.forEach((img, i) => {
      if (!isDesktopNow) {
        img.classList.remove('active');
        img.style.display = '';
        return;
      }
      if (prefersReducedMotion) {
        img.classList.toggle('active', i === index);
        img.style.display = i === index ? '' : 'none';
      } else {
        img.classList.remove('active');
        img.style.display = 'none';
        if (i === index) {
          img.classList.add('active');
          img.style.display = '';
        }
      }
    });

    videos.forEach((video, i) => {
      if (!isDesktopNow) {
        video.classList.remove('active');
        video.pause();
        video.style.display = 'none';
        return;
      }
      if (prefersReducedMotion) {
        video.classList.remove('active');
        video.pause();
        video.style.display = 'none';
      } else {
        video.style.display = '';
        if (i === index) {
          video.classList.add('active');
          video.currentTime = 0;
          if (video.readyState >= 3) {
            void video.play();
          } else {
            video.addEventListener('canplay', function handler() {
              void video.play();
              video.removeEventListener('canplay', handler);
            });
          }
        } else {
          video.classList.remove('active');
          video.pause();
          video.currentTime = 0;
        }
      }
    });

    currentActiveIndex = index;
  }

  function enableDesktopLocking(): void {
    let isLocked = false;
    let blockWasScrolledPast = false;
    let touchStartY = 0;

    function lockSection(): void {
      if (isLocked) return;
      gsap.set('body', { overflow: 'hidden' });
      isLocked = true;

      window.addEventListener('wheel', wheelHandler, { passive: false });
      window.addEventListener('touchstart', touchStartHandler, { passive: false });
      window.addEventListener('touchmove', touchMoveHandler, { passive: false });
    }

    function unlockSection(): void {
      if (!isLocked) return;
      gsap.set('body', { overflow: '' });
      isLocked = false;

      window.removeEventListener('wheel', wheelHandler);
      window.removeEventListener('touchstart', touchStartHandler);
      window.removeEventListener('touchmove', touchMoveHandler);
    }

    function isFirstCardVisible(): boolean {
      return rightCards.scrollTop <= 0;
    }

    function isLastCardVisible(): boolean {
      const { scrollTop, scrollHeight, clientHeight } = rightCards;
      return scrollHeight - scrollTop - clientHeight <= 1;
    }

    const touchStartHandler = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const touchMoveHandler = (e: TouchEvent) => {
      if (!isLocked) return;

      // Prevent the browser's default scroll/drag behavior
      e.preventDefault();

      const currentY = e.touches[0].clientY;
      const deltaY = touchStartY - currentY;

      touchStartY = currentY;

      const isScrollingDown = deltaY > 0;
      const isScrollingUp = deltaY < 0;

      // Check for unlock conditions, same as the wheel handler
      if (isScrollingDown && isLastCardVisible()) {
        unlockSection();
        blockWasScrolledPast = true;
        return;
      }
      if (isScrollingUp && isFirstCardVisible()) {
        unlockSection();
        blockWasScrolledPast = false;
        return;
      }


      rightCards.scrollTop += deltaY;
    };

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();

      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      if (isScrollingDown && isLastCardVisible()) {
        unlockSection();
        blockWasScrolledPast = true;
        return;
      }
      if (isScrollingUp && isFirstCardVisible()) {
        unlockSection();
        blockWasScrolledPast = false;
        return;
      }

      rightCards.scrollTop += e.deltaY;
    };

    function onScroll(): void {
      const blockRect = block.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const currentY = window.scrollY;
      const isScrollingDown = currentY > lastScrollY;
      const isScrollingUp = currentY < lastScrollY;

      const blockCenter = blockRect.top + blockRect.height / 2;
      const isCentered = Math.abs(blockCenter - viewportHeight / 2) < 50;

      if (isLocked) {
        lastScrollY = currentY;
        return;
      }

      if (isScrollingDown && isCentered && !blockWasScrolledPast) {
        lockSection();
      }

      if (isScrollingUp && isCentered && blockWasScrolledPast) {
        lockSection();
      }

      lastScrollY = currentY;
    }
    
    const ACTIVATION_THRESHOLD = 0.5;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Array.from(cards).indexOf(entry.target as HTMLDivElement);
          if (idx !== -1) {
            ratios[idx] = entry.intersectionRatio;
          }
        });
        let maxRatio = ACTIVATION_THRESHOLD;
        let maxIndex = currentActiveIndex;
        ratios.forEach((ratio, idx) => {
          if (ratio >= maxRatio) {
            maxRatio = ratio;
            maxIndex = idx;
          }
        });
        if (maxIndex !== currentActiveIndex) {
          setActiveMedia(maxIndex);
        }
      },
      {
        root: rightCards,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    cards.forEach((card) => observer.observe(card));
    setActiveMedia(0);

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);

    cleanupFunction.push(() => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      unlockSection();
      observer.disconnect();
    });
  }

  function disableDesktopLocking(): void {
    cleanupFunction.forEach((fn) => fn());
    cleanupFunction = [];
    gsap.set('body', { overflow: '' });
  }

  window.addEventListener('DOMContentLoaded', () => {
    setActiveMedia(currentActiveIndex);
  });
  window.addEventListener('resize', () => {
    setActiveMedia(currentActiveIndex);
  });

  if (isDesktop) {
    enableDesktopLocking();
  }

  window.addEventListener('resize', () => {
    const nowDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (nowDesktop && !isDesktop) {
      enableDesktopLocking();
    } else if (!nowDesktop && isDesktop) {
      disableDesktopLocking();
    }
    isDesktop = nowDesktop;
  });
};

export default ColorMediaColumns;
